import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { api } from "@/lib/auth";
import { moonTonight, cycleKey } from "./MoonRitual";
import { tonightsMoonrise, fmtMoonrise } from "@/lib/moonrise";

const COPY = {
  full: ["Full Moon", "Pause with the temple for three minutes.", "Begin the practice"],
  new: ["New Moon", "Plant one intention in the dark.", "Begin the practice"],
};
const bannerKey = (kind) => `${cycleKey(kind)}-banner`;

export default function MoonBanner() {
  const [kind, setKind] = useState(null);
  useEffect(() => {
    const k = moonTonight();
    if (!k || localStorage.getItem(bannerKey(k))) return undefined;
    if (!process.env.REACT_APP_BACKEND_URL) {
      setKind(k);
      return undefined;
    }
    let alive = true;
    api.get("/site-settings")
      .then(({ data }) => alive && data?.[k === "full" ? "full_card" : "new_card"] !== false && setKind(k))
      .catch(() => alive && setKind(k));
    return () => {
      alive = false;
    };
  }, []);
  const [rise, setRise] = useState(null);
  useEffect(() => {
    if (kind !== "full") return undefined;
    let alive = true;
    tonightsMoonrise()
      .then((t) => alive && setRise(t))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [kind]);
  const dismiss = () => {
    localStorage.setItem(bannerKey(kind), "1");
    setKind(null);
  };
  const begin = () => window.dispatchEvent(new CustomEvent("tt:moon-ritual", { detail: kind }));
  const [title, line, cta] = COPY[kind] ?? [];
  return (
    <AnimatePresence initial={false}>
      {kind && (
        <motion.div
          key="moon-banner"
          data-testid="moon-banner"
          data-moon={kind}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden border-b border-[#D9B47A]/15 bg-[#120C0A]"
        >
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10 h-9 flex items-center gap-3 text-[12px]">
            <span aria-hidden="true" className={`shrink-0 w-3 h-3 rounded-full ${kind === "full" ? "bg-[#F2E8DF] shadow-[0_0_10px_rgba(242,232,223,0.7)]" : "border border-[#D9B47A]/70 bg-[#0A0706]"}`} />
            <p className="min-w-0 truncate text-[#CFC2B6]">
              <span className="font-tt-mono uppercase tracking-[0.2em] text-[#D9B47A]">{title}<span className="hidden sm:inline"> tonight</span></span>
              <span className="hidden sm:inline"> · {line}</span>
              {rise && (
                <span data-testid="moon-banner-rise" className="text-[#F2E8DF]">
                  {" · "}{rise <= new Date() ? "Rose " : <><span className="sm:hidden">Rises </span><span className="hidden sm:inline">Moonrise </span></>}{fmtMoonrise(rise)}
                </span>
              )}
            </p>
            <button data-testid="moon-banner-begin" onClick={begin} className="shrink-0 ml-auto font-tt-mono uppercase tracking-[0.18em] text-[#F2E8DF] underline decoration-[#D9B47A]/60 underline-offset-4 hover:text-[#D9B47A] transition-colors">
              <span className="sm:hidden">Begin</span><span className="hidden sm:inline">{cta}</span>
            </button>
            <button data-testid="moon-banner-dismiss" onClick={dismiss} aria-label="Dismiss" className="shrink-0 p-1 text-[#A8998C] hover:text-[#F2E8DF] transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
